import React from 'react';
import { useGetMeteocodeByCodeStringQuery } from 'Services/api/meteoCodesApi';
import { format } from 'date-fns';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import { Pane, Tab, Tablist } from 'evergreen-ui';
import { DATE_FORMATS } from 'Utils/formats';
import { DataValueTS, DataValueTSFull, SeriesValue } from './types';
import Measurement from './EditData.Measurement';
import Series from './EditData.Series';

const newTSValue = ({
  dateTime,
  sourceType,
  sourceId,
  meteoCode,
  place,
  stationEntityId,
}: DataValueTS): DataValueTSFull => ({
  isNew: true,
  dateTime: format(new Date(dateTime), DATE_FORMATS.api),
  sourceType,
  sourceId,
  meteoCode,
  qc: 100,
  place: place || '',
  point: -1,
  stationEntityId: stationEntityId || -1,
  valueType: 'REAL',
  dataValueComment: null,
});

export type EditTimeSeriesDataProps = {
  data: {
    columnData: {
      meteocode: string
      label: string
      entityName?: string
      stationEntity?: number
      description: string
      placeName?: string
      unit?: string
    },
    dataType: string
    dateTime: string
    indexes: {
      rowIndex: number
      columnIndex: number
    }
    values: DataValueTSFull[]
    sourceId?:number
  }
  sourceId: number
  zone: string
}

export default function EditTimeSeriesData({ data, sourceId, zone }: EditTimeSeriesDataProps) {
  const {
    data: meteocode = {}, isFetching: isLoadingMeteocodes,
  } = useGetMeteocodeByCodeStringQuery(data.columnData.meteocode, { skip: !data.columnData.meteocode });

  const [values, setValues] = React.useState<DataValueTSFull[]>(data.values);
  const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);

  const newValue = React.useMemo(() => {
    return newTSValue({
      dateTime: data.dateTime,
      sourceType: data.dataType,
      meteoCode: {
        code: meteocode.meteoCodeHash,
        codeString: meteocode.meteoCodeString,
        name: meteocode.name,
      },
      place: data.columnData.placeName,
      sourceId: sourceId || data.sourceId,
      stationEntityId: data.columnData.stationEntity,
    });
  }, [
    data.columnData.placeName,
    data.columnData.stationEntity,
    data.dataType,
    data.dateTime,
    data.sourceId,
    meteocode.meteoCodeHash,
    meteocode.meteoCodeString,
    meteocode.name,
    sourceId,
  ]);

  const onChangeValue = React.useCallback((index: number, value: DataValueTSFull) => {
    setValues((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      if (index === -1) {
        tmp.push(value);
      } else {
        tmp[index] = value;
      }
      return tmp;
    });
  }, []);

  const addNewTSValue = React.useCallback(() => {
    setValues((cur) => [...cur, { ...newValue, point: null }]);
  }, [newValue]);

  const [measurement, series, measurementIndex] = React.useMemo(() => {
    let measurementValue = newValue;
    const seriesValues: SeriesValue[] = [];
    let measurementValueIndex = -1;

    values.forEach((value, index) => {
      if (value.point === -1) {
        measurementValue = value;
        measurementValueIndex = index;
        return;
      }
      seriesValues.push({ value, index });
    });
    return [measurementValue, seriesValues, measurementValueIndex];
  }, [newValue, values]);

  if (isLoadingMeteocodes) return <SuspenseSpinner />;

  return (
    <Pane display="grid" gridTemplateRows="max-content 1fr" rowGap=".5rem" minHeight={350}>
      <Tablist>
        <Tab
          isSelected={selectedTabIndex === 0}
          onSelect={() => setSelectedTabIndex(0)}
        >
          Измерение
        </Tab>
        <Tab
          isSelected={selectedTabIndex === 1}
          onSelect={() => setSelectedTabIndex(1)}
        >
          Ряд
        </Tab>
      </Tablist>
      {selectedTabIndex === 0 && (
        <Measurement
          measurement={measurement}
          zone={zone}
          index={measurementIndex}
          onChangeValue={onChangeValue}
        />
      )}
      {selectedTabIndex === 1 && (
        <Series
          seriesValues={series}
          zone={zone}
          onChangeValue={onChangeValue}
          addNewTSValue={addNewTSValue}
        />
      )}
    </Pane>
  );
}
